import axios from 'axios';

/**
 * Em desenvolvimento o CRA faz proxy de /api para localhost:3001 (campo "proxy"
 * no package.json), e o docker-compose faz o mesmo pelo nginx. Por isso o caminho
 * relativo funciona nesses dois casos.
 *
 * No Railway não há proxy: o painel e a API são serviços com domínios próprios, e
 * REACT_APP_API_URL (embutida no bundle em tempo de build) aponta para a API.
 */
export const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

/**
 * Mensagem de erro de uma chamada à API.
 *
 * A distinção que importa: quando `err.response` existe, o servidor respondeu e a
 * mensagem dele é a explicação. Quando não existe, a requisição não chegou —
 * REACT_APP_API_URL errada, API fora do ar, ou o CORS do backend não liberando o
 * domínio do painel. Sem separar os dois casos, um problema de configuração
 * aparece como "Erro ao fazer login" e não há por onde começar a investigar.
 */
export function apiErrorMessage(err, fallback = 'Não foi possível concluir a operação') {
  if (err?.response) return err.response.data?.error || fallback;

  return (
    `Não foi possível falar com a API em ${API_BASE_URL}. ` +
    'Verifique se ela está no ar, se REACT_APP_API_URL está correta (exige rebuild do ' +
    'painel para valer) e se FRONTEND_URL no backend libera o domínio deste painel.'
  );
}

/**
 * Loja ativa do painel.
 *
 * Quem administra um grupo ou uma concessionária tem várias lojas no escopo. As
 * telas de leitura somam todas, mas cadastrar visita, vendedor ou veículo precisa
 * de destino único — e é este id que o backend usa como destino.
 *
 * Fica em módulo, e não em contexto React, porque quem precisa dele é o
 * interceptor: assim TODA chamada leva o parâmetro, sem depender de cada página
 * lembrar de passá-lo.
 */
const ACTIVE_STORE_KEY = 'crm_active_store';

let activeStoreId = localStorage.getItem(ACTIVE_STORE_KEY) || null;

export function setActiveStoreId(id) {
  activeStoreId = id ? String(id) : null;
  if (activeStoreId) localStorage.setItem(ACTIVE_STORE_KEY, activeStoreId);
  else localStorage.removeItem(ACTIVE_STORE_KEY);
}

export function getActiveStoreId() {
  return activeStoreId;
}

// Anexa o JWT guardado no login e a loja ativa.
// Espelho do limite do backend: requisições disparadas com ninguém mexendo na
// tela há mais de 1 minuto (o inbox recarrega sozinho) vão marcadas como fundo,
// e o servidor não as conta como atividade — senão a sessão nunca expiraria
// com o inbox aberto.
const LAST_ACTIVITY_KEY = 'crm_last_activity';
const BACKGROUND_AFTER_MS = 60 * 1000;
export const INACTIVITY_LOGOUT_FLAG = 'crm_inactivity_logout';

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('crm_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;

  const lastActivity = Number(localStorage.getItem(LAST_ACTIVITY_KEY)) || 0;
  if (token && Date.now() - lastActivity > BACKGROUND_AFTER_MS) {
    config.headers['X-Background'] = '1';
  }

  // O login não tem sessão ainda, e uma chamada que já traz organization_id
  // explícito (o superadmin inspecionando um cliente) manda mais que o padrão.
  const isLogin = config.url?.includes('/auth/login');
  if (activeStoreId && !isLogin && config.params?.organization_id === undefined) {
    config.params = { ...config.params, organization_id: activeStoreId };
  }

  return config;
});

// Sessão expirada (8h) → limpa o estado e volta para a tela de login.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const isLoginRequest = error.config?.url?.includes('/auth/login');
    if (error.response?.status === 401 && !isLoginRequest) {
      // O servidor derrubou por inatividade: a tela de login explica o motivo.
      if (error.response.data?.code === 'INACTIVITY') {
        localStorage.setItem(INACTIVITY_LOGOUT_FLAG, '1');
      }
      localStorage.removeItem(LAST_ACTIVITY_KEY);
      localStorage.removeItem('crm_token');
      localStorage.removeItem('crm_user');
      localStorage.removeItem(ACTIVE_STORE_KEY);
      activeStoreId = null;
      window.location.reload();
    }
    return Promise.reject(error);
  }
);

export const authApi = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  // O token vai explícito: o handleLogout apaga o localStorage logo em seguida,
  // e o interceptor (assíncrono) leria o storage já vazio — a chamada sairia
  // sem autenticação e o servidor nunca liberaria o próximo login.
  // Corpo `{}` de propósito: com `null` o axios manda a string "null", que o
  // express.json (modo estrito) recusa — o logout devolvia 500 e o session_id
  // não era limpo, então o próximo login desse usuário esperava os 5 minutos
  // de inatividade.
  logout: (token) => api.post('/auth/logout', {}, { headers: { Authorization: `Bearer ${token}` } }),
  me: () => api.get('/auth/me'),
  updateMe: (data) => api.patch('/auth/me', data),
};

export const dashboardApi = {
  getStats: () => api.get('/dashboard/stats'),
  // { seller_ids: '1,2', from: '2026-01-01', to: '2026-01-31' } — todos opcionais.
  getSellerPerformance: (params) => api.get('/dashboard/seller-performance', { params }),
  getVisitsByStatus: (params) => api.get('/dashboard/visits-by-status', { params }),
};

// Relatório do administrador. Aceita { from, to } no formato 'AAAA-MM-DD';
// sem eles, o backend usa os últimos 30 dias.
export const reportsApi = {
  sellerConversion: (params) => api.get('/reports/seller-conversion', { params }),
  unattended: (params) => api.get('/reports/unattended', { params }),
  // { from, to, seller_ids: '1,2', status: open|expired|accepted|rejected }
  proposals: (params) => api.get('/reports/proposals', { params }),
};

export const visitsApi = {
  list: (params) => api.get('/visits', { params }),
  // `organizationId` explícito é do reenvio de rascunho offline: a visita entra
  // na loja onde foi capturada, mesmo que a loja ativa tenha mudado nesse
  // meio-tempo (parâmetro explícito vence o interceptor).
  create: (data, organizationId) =>
    api.post('/visits', data, organizationId ? { params: { organization_id: organizationId } } : undefined),
  updateStatus: (id, status) => api.patch(`/visits/${id}/status`, { status }),
  update: (id, data) => api.patch(`/visits/${id}`, data),
  // Antecipa o follow-up pendente para agora; quem envia continua sendo o cron
  // do backend (até 5 minutos). Só admin.
  sendFollowupNow: (id) => api.post(`/visits/${id}/followup/now`),
};

// Ficha do cliente: a pessoa com as visitas, os interesses e as conversas dela.
export const clientsApi = {
  profile: (id) => api.get(`/clients/${id}`),
  // Só admin: apaga o cliente e todo o histórico dele (visitas, conversas,
  // mensagens, propostas, test-drives).
  remove: (id) => api.delete(`/clients/${id}`),
};

export const vehiclesApi = {
  list: (params) => api.get('/vehicles', { params }),
  create: (data) => api.post('/vehicles', data),
  update: (id, data) => api.patch(`/vehicles/${id}`, data),
  remove: (id) => api.delete(`/vehicles/${id}`),
  // Fotos: multipart com várias no campo "photos"; o backend devolve a lista
  // completa (id/posição) do veículo. O binário volta como blob porque a rota
  // é autenticada (JWT no header) — um <img src> direto para a API daria 401.
  uploadPhotos: (id, files) => {
    const form = new FormData();
    files.forEach((file) => form.append('photos', file, file.name));
    return api.post(`/vehicles/${id}/photos`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  photo: (id, photoId) => api.get(`/vehicles/${id}/photos/${photoId}`, { responseType: 'blob' }),
  deletePhoto: (id, photoId) => api.delete(`/vehicles/${id}/photos/${photoId}`),
  // Materiais do veículo (kind = commercial | datasheet): título + texto e/ou
  // arquivo (PDF, JPG, PNG, MP4), um por chamada; o backend devolve a lista
  // completa do veículo.
  addPaymentTerm: (id, { kind = 'commercial', title, description, file }) => {
    const form = new FormData();
    form.append('kind', kind);
    form.append('title', title);
    form.append('description', description || '');
    if (file) form.append('file', file, file.name);
    return api.post(`/vehicles/${id}/payment-terms`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  // Edição: texto por JSON; com arquivo novo vai em multipart (anexa ou troca).
  updatePaymentTerm: (id, termId, { title, description, file }) => {
    if (!file) return api.patch(`/vehicles/${id}/payment-terms/${termId}`, { title, description });
    const form = new FormData();
    form.append('title', title);
    form.append('description', description || '');
    form.append('file', file, file.name);
    return api.patch(`/vehicles/${id}/payment-terms/${termId}`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  paymentTermFile: (id, termId) => api.get(`/vehicles/${id}/payment-terms/${termId}/file`, { responseType: 'blob' }),
  deletePaymentTerm: (id, termId) => api.delete(`/vehicles/${id}/payment-terms/${termId}`),
};

// Propostas: /proposals devolve { proposals }; criar exige loja ativa (o
// interceptor anexa organization_id). preview monta o texto sem gravar.
export const proposalsApi = {
  list: (params) => api.get('/proposals', { params }),
  get: (id) => api.get(`/proposals/${id}`),
  preview: (data) => api.post('/proposals/preview', data),
  create: (data) => api.post('/proposals', data),
  update: (id, data) => api.patch(`/proposals/${id}`, data),
  remove: (id) => api.delete(`/proposals/${id}`),
  send: (id) => api.post(`/proposals/${id}/send`),
  // Blob: a rota é autenticada, um <a href> direto voltaria 401.
  pdf: (id) => api.get(`/proposals/${id}/pdf`, { responseType: 'blob' }),
};

// Test-drives: agendamento manual pelo painel; a IA agenda pela conversa e o
// resultado aparece na mesma lista. Horários no formato do datetime-local
// ("2026-09-26T15:00"), lidos pelo backend como horário de Brasília.
export const testDrivesApi = {
  list: (params) => api.get('/test-drives', { params }),
  create: (data) => api.post('/test-drives', data),
  update: (id, data) => api.patch(`/test-drives/${id}`, data),
  resendConfirmation: (id) => api.post(`/test-drives/${id}/confirmation`),
  remove: (id) => api.delete(`/test-drives/${id}`),
};

export const conversationsApi = {
  list: () => api.get('/conversations'),
  getMessages: (id) => api.get(`/conversations/${id}/messages`),
  send: (id, message) => api.post(`/conversations/${id}/messages`, { message }),
  // Anexo de documento (.pdf/.docx/.xlsx). O 'multipart/form-data' explícito
  // faz o axios trocar o application/json padrão da instância pelo multipart
  // com boundary do navegador.
  sendAttachment: (id, file) => {
    const form = new FormData();
    form.append('file', file);
    return api.post(`/conversations/${id}/attachments`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  // Blob porque o download é autenticado (JWT no header) — um <a href> direto
  // para a API voltaria 401.
  // Áudio gravado no inbox. O nome leva a extensão do que o navegador gravou
  // só para o multipart ter um nome; o backend lê o contêiner dos bytes.
  sendAudio: (id, blob) => {
    const form = new FormData();
    const ext = /ogg/.test(blob.type) ? 'ogg' : /mp4|aac/.test(blob.type) ? 'm4a' : 'webm';
    form.append('file', blob, `audio.${ext}`);
    return api.post(`/conversations/${id}/audio`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  downloadAttachment: (id, attachmentId) =>
    api.get(`/conversations/${id}/attachments/${attachmentId}`, { responseType: 'blob' }),
  // Condições de pagamento pré-prontas da LOJA DA CONVERSA: { vehicles: [{..., terms}] }.
  paymentTerms: (id) => api.get(`/conversations/${id}/payment-terms`),
  sendPaymentTerm: (id, termId) => api.post(`/conversations/${id}/payment-terms`, { term_id: termId }),
  handoffToSeller: (id, sellerId, message) =>
    api.post(`/conversations/${id}/handoff`, { seller_id: sellerId, message }),
  returnBot: (id) => api.post(`/conversations/${id}/return-bot`),
  close: (id) => api.post(`/conversations/${id}/close`),
};

// Web Push (services/push.js faz a costura com o navegador). O token explícito
// no unsubscribe é para o logout, pelo mesmo motivo do authApi.logout: o
// localStorage já foi limpo quando o interceptor roda.
export const pushApi = {
  config: () => api.get('/push/config'),
  subscribe: (subscription) => api.post('/push/subscriptions', { subscription }),
  unsubscribe: (endpoint, token = null) =>
    api.delete('/push/subscriptions', {
      data: { endpoint },
      ...(token ? { headers: { Authorization: `Bearer ${token}` } } : {}),
    }),
  test: () => api.post('/push/test', {}),
};

export const sellersApi = {
  // Sem parâmetros traz só os ativos (usado no select de Nova Visita);
  // { include_inactive: true } traz todos (tela de Usuários, relatórios).
  // Não há create: desde 03/11/2026 o vendedor nasce do usuário com perfil
  // "Vendedor" (POST /users). O update ficou para o vendedor sem login.
  list: (params) => api.get('/sellers', { params }),
  update: (id, data) => api.patch(`/sellers/${id}`, data),
};

export const usersApi = {
  list: () => api.get('/users'),
  create: (data) => api.post('/users', data),
  // `organizationId` é para o superadmin, que não tem organização própria: sem ele
  // o backend não sabe de qual cliente é o usuário e recusa a requisição. Um admin
  // pode omitir — o escopo dele vem da própria conta.
  update: (id, data, organizationId) =>
    api.patch(`/users/${id}`, data, organizationId ? { params: { organization_id: organizationId } } : undefined),
};

// Pós-venda: quem comprou (test-drive + proposta aceita) e a oferta de
// serviços da loja. /aftersales devolve { clients, services?, auto?,
// delay_days?, template?, preview? } (os opcionais só com loja ativa);
// send manda a um ou mais clientes e devolve { results } um a um.
export const aftersalesApi = {
  list: () => api.get('/aftersales'),
  send: (data) => api.post('/aftersales/send', data),
};

export const settingsApi = {
  get: () => api.get('/settings'),
  update: (key, value) => api.put(`/settings/${key}`, { value }),
};

// Assistente de IA: se a plataforma tem provedor configurado, e o simulador
// (conversa de teste na loja ativa, sem cliente real e sem gravar nada).
export const aiApi = {
  status: () => api.get('/ai/status'),
  sandbox: (data) => api.post('/ai/sandbox', data),
};

// Marcas de veículo atuantes no Brasil. Vem da API para que atualizar a lista não
// exija rebuild do painel (o CRA embute as constantes do frontend no bundle).
export const brandsApi = {
  list: () => api.get('/brands'),
};

// Tabela FIPE (proxy no backend): marca → modelo → ano → valor de tabela.
export const fipeApi = {
  brands: () => api.get('/fipe/brands'),
  models: (brand) => api.get(`/fipe/brands/${brand}/models`),
  years: (brand, model) => api.get(`/fipe/brands/${brand}/models/${model}/years`),
  price: (brand, model, year) => api.get(`/fipe/brands/${brand}/models/${model}/years/${year}`),
};

// Opcionais do veículo, agrupados: [{ group, items: [rótulo, ...] }].
export const vehicleFeaturesApi = {
  list: () => api.get('/vehicle-features'),
  fuels: () => api.get('/vehicle-fuels'), // array direto de rótulos
};

// Consulta pública de CNPJ, para o botão "Buscar CNPJ" preencher o cadastro.
// Passa pelo backend: o provedor externo pode mudar sem rebuild do painel.
export const cnpjApi = {
  lookup: (cnpj) => api.get(`/cnpj/${String(cnpj).replace(/\D/g, '')}`),
};

// Cadastro de clientes (grupo / concessionária / loja) — só o superadmin enxerga
// list/create/update. A rota /scope vale para qualquer papel.
export const organizationsApi = {
  list: () => api.get('/organizations'),
  create: (data) => api.post('/organizations', data),
  update: (id, data) => api.patch(`/organizations/${id}`, data),
  // As lojas que o usuário logado pode operar — alimenta o seletor de loja.
  scope: () => api.get('/organizations/scope'),
  // O admin de loja usa 'me': o backend resolve para a própria loja dele. Quem
  // administra um grupo passa o id da loja escolhida.
  getWhatsapp: (id = 'me') => api.get(`/organizations/${id}/whatsapp`),
  updateWhatsapp: (id, data) => api.put(`/organizations/${id}/whatsapp`, data),
  // Logo da empresa (PNG/JPG até 2MB), usada no cabeçalho do PDF da proposta.
  // Blob porque a rota é autenticada.
  uploadLogo: (id, file) => {
    const form = new FormData();
    form.append('file', file, file.name);
    return api.post(`/organizations/${id}/logo`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  logo: (id) => api.get(`/organizations/${id}/logo`, { responseType: 'blob' }),
  deleteLogo: (id) => api.delete(`/organizations/${id}/logo`),
};

export default api;
